import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getMasterShipperOptions } from '@/services/master/userManagement/apis'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { shipperScopeOf } from '@/components/system/shipperScope'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { usePboQueryErrorToast } from '@/hooks/usePboQueryErrorToast'
import { appendedRows, FETCH_ALL_MESSAGES } from '@/hooks/useFetchAllRows'
import { failureError, invalidError, notice } from '@/components/pbo/notice'
import { commonCodeQueryKeys, useGetCommonCodeList } from './queries/useGetCommonCodeList'
import type { CommonCodeParams, CommonCodeRow } from './queries/useGetCommonCodeList'
import { useSaveCommonCodes } from './queries/useSaveCommonCodes'
import { useDeleteCommonCodes } from './queries/useDeleteCommonCodes'
import { fromSearchQuery } from './queries/searchQuery'
import { BE_REQUIRED_KEYS, GRID_CDS, MESSAGES, SAVE_REQUIRED_CANDIDATES, TABLE_ID } from './consts'
import { findMissingRequired, requiredTitles, withRequired } from '@/utils/requiredCells'
import CommonCodePageView, { type ActionCtx, type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'

/**
 * 코드관리(SY07) — 1.0 SY07 을 pbo-core 로 재구현한 화면(dev 라우트 sy10).
 *
 * 겉(조회영역·표·확인창)은 컨덕터 뷰(`./handoff/page.generated`)가 그리고, 이 파일은 옛 훅·API·세션/URL 유지를
 * 그대로 두고 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드, 컨덕터 문서 51). 옛 조회영역·표 컴포넌트(`components/`)와
 * 플래그 분기(useFeatureFlag + v2/page)는 없앴다 — 화면은 하나다(2026-09-11 규칙). 데이터 훅(`queries/`)은 그대로다.
 *
 * 화주 잠금 — 통합 운영 화주(FREE_SHIPPERS 4종)만 화주를 고른다. 화주 고정 계정은 옵션·URL 복원값·조회 조건·기본값·저장 행의 화주코드가
 * 전부 현재 화주로 묶인다(옛 화면은 기본값만 세션 화주였다 — 권한 밖 화주 조회·저장이 새는 구멍이라 여기서 막는다. gr02·rgr10 규약).
 *
 * [추가](sy07AddButton 슬롯, 컨덕터 #592) — 빈 행을 맨 위에 넣는다(화주 = 조회 화주, __new 로 필수 미입력 표기).
 * [저장]은 새 행을 POST, 기존 행을 PUT 으로, [삭제]는 DELETE 로 행별 순차 반영한다(LOGISPRD-14065). 새 행 삭제는 화면에서만 지운다.
 */
/** 표 행 — key = BE 행 id(문자열). 뷰의 dataSource 스프레드(`{ key: i, ...r }`)가 이 key 로 인덱스를 덮고, 선택·편집 왕복도 이 key 로 되찾는다. */
/** _id 없음 = [추가]로 생긴 미저장 행(저장 때 등록 POST). __new = DS 표 계약(처음부터 필수 미입력을 빨갛게 표기, 데모와 같다). */
type EditableRow = Omit<CommonCodeRow, '_id'> & { _id?: number } & Row & { key: string; __new?: boolean }
type SavedRow = EditableRow & { _id: number }
type ShipperOption = { value: string; label: string }

const toEditable = (row: CommonCodeRow): EditableRow => ({ ...row, key: String(row._id) })

/** 조회영역 값(키 = filterKey) → BE 조회 조건. 데모는 코드그룹·코드가 단건 입력이다(옛 화면은 다건 — 데모가 정본). */
const toParams = (filters: Record<string, unknown>): CommonCodeParams => {
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
  const one = (v: unknown) => {
    const t = text(v)
    return t ? [t] : undefined
  }
  return { shipperCode: text(filters.SPR_NM), codeGrps: one(filters.CODE_GRP), codes: one(filters.CODE) }
}

/** 되살린 조회 조건 → 조회영역 값(키 = filterKey). 없는 값은 얹지 않는다(기본값이 살아 있게). 다건은 첫 값만(입력이 단건). */
const toFilterValues = (params: CommonCodeParams | undefined): Record<string, unknown> => {
  if (!params) return {}
  return {
    ...(params.shipperCode ? { SPR_NM: params.shipperCode } : {}),
    ...(params.codeGrps?.[0] ? { CODE_GRP: params.codeGrps[0] } : {}),
    ...(params.codes?.[0] ? { CODE: params.codes[0] } : {}),
  }
}

/** BE meta.message 우선, 빈 body 응답(게이트웨이/인프라 차단 등)은 HTTP 상태를 함께 노출 — 옛 CommonCodeTable 규약. */
const apiErrMessage = (e: unknown) => {
  const resp = (e as { response?: { status?: number; data?: { meta?: { message?: string } } } })?.response
  return resp?.data?.meta?.message ?? (resp?.status ? `처리에 실패했습니다. (HTTP ${resp.status})` : '처리에 실패했습니다.')
}

/** 순차 저장·삭제 훅이 실패 오류에 싣는 앞선 반영 건수 */
const doneOf = (e: unknown) => (e as { done?: number })?.done ?? 0

const hasCondition = (p: CommonCodeParams) => Object.values(p).some((v) => (Array.isArray(v) ? v.length > 0 : v != null))
const rowKeyOf = (r: Row) => String((r.key as string | number | undefined) ?? '')
/** 조회 조건이 같은가 — 같은 조건이면 캐시라 요청이 안 나가므로 그때만 무효화한다(다르면 새 키 쿼리가 스스로 받는다). */
const sameParams = (a: CommonCodeParams, b: CommonCodeParams) => JSON.stringify(a) === JSON.stringify(b)

function CommonCodeManagement() {
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()

  // 계정은 마운트 시점 한 번 — 화주 전환은 reload 를 동반해 새 마운트를 만든다. 기준은 shipperCode(현재 화주, 전환 반영)다(gr02 규약).
  //  잠금 판정은 2.0 공용 규칙 — FREE_SHIPPERS 4종 외는 자기 화주로 고정(LOGISPRD-14652).
  const accountRef = useRef<{ currentShipper: string; isFreeShipper: boolean }>()
  if (!accountRef.current) {
    const scope = shipperScopeOf(AuthStorage.user.get())
    accountRef.current = { currentShipper: scope.shipperCode, isFreeShipper: !scope.locked }
  }
  const { currentShipper, isFreeShipper } = accountRef.current
  /** 화주 고정 계정이면 어떤 경로(URL 복원·조회영역)로 와도 현재 화주로 덮는다. */
  const lockShipper = useCallback(
    (p: CommonCodeParams): CommonCodeParams => (isFreeShipper ? p : { ...p, shipperCode: currentShipper }),
    [isFreeShipper, currentShipper],
  )

  // 마운트 스냅샷은 useRef 로 — useMemo 는 캐시라 React 가 언제든 버릴 수 있다. 복원값도 잠금을 지난다.
  const restoredRef = useRef<CommonCodeParams | undefined>()
  const restoredReadRef = useRef(false)
  if (!restoredReadRef.current) {
    restoredReadRef.current = true
    const restored = fromSearchQuery(parsedQs)
    restoredRef.current = restored ? lockShipper(restored) : undefined
  }

  /** 화주 옵션 — 유저관리와 같은 API 재사용. 화주 고정 계정은 자기 화주만 남긴다. */
  const shipperQuery = useQuery<ShipperOption[], Error>({
    queryKey: commonCodeQueryKeys.shipperOptions,
    queryFn: getMasterShipperOptions,
    meta: { noErrorMessage: true }, // 전역 QueryCache.onError 알럿을 끄고 아래 화면 문구만 낸다(두 번 뜨지 않게, 리뷰 gyoungjun)
  })
  const shipperOptions = useMemo<ShipperOption[]>(() => {
    const all = shipperQuery.data ?? []
    return isFreeShipper ? all : all.filter((o) => o.value === currentShipper)
  }, [shipperQuery.data, isFreeShipper, currentShipper])
  // 기본 화주 = 현재 화주. 통합 운영 계정은 목록에 없으면 첫 화주로 폴백(옛 화면 규약), 화주 고정 계정은 폴백 없이 자기 화주다.
  // 화주 목록 실패 — 옵션이 비면 화주를 못 고른다. 화면은 그리되 이유를 알린다(ma03 과 같은 규약).
  //  목록 조회와 같은 훅 — 재조회가 다시 실패해도(errorUpdatedAt 갱신) 다시 알린다.
  usePboQueryErrorToast(shipperQuery, MESSAGES.shipperLoadFailed, { preferFallback: true }) // 화면 문구 고정 — BE 문구로 덮이면 셀렉트가 왜 비었는지 모른다
  const defaultShipper = useMemo(() => {
    if (!isFreeShipper) return currentShipper
    return String((shipperOptions.find((o) => o.value === currentShipper) ?? shipperOptions[0])?.value ?? '')
  }, [shipperOptions, isFreeShipper, currentShipper])

  /** 마지막 조회 조건을 메뉴 세션·URL 에 남긴다. reload() 를 빼면 탭 스트립이 옛 조건을 되살린다. */
  const persist = useCallback(
    (next: CommonCodeParams) => {
      MenuStorage.menuOnSession.setSearch(next)
      menuSession.reload()
      replaceQuery(next)
    },
    [menuSession, replaceQuery],
  )

  /** 진입 자동조회 — 1.0 필수 필터 동작. URL 조건이 있으면 그것, 없으면 기본 화주다(옛 CommonCodeFilter 의 마운트 onSearch 자리). */
  const [params, setParams] = useState<CommonCodeParams>(() => restoredRef.current ?? {})
  const bootedRef = useRef(!!restoredRef.current)
  useEffect(() => {
    if (bootedRef.current || !defaultShipper) return
    bootedRef.current = true
    const next = { shipperCode: defaultShipper }
    persist(next)
    setParams(next)
  }, [defaultShipper, persist])

  /** 빈 조건(초기·초기화)에서는 조회하지 않는다 — 조건 없는 전체 조회 방지(MA02·GL11 동일 게이팅). */
  const { rows: fetchedRows, isFirstLoading, isFetchingRest, guardAction } = useGetCommonCodeList(params, hasCondition(params))
  const saveCodes = useSaveCommonCodes()
  const deleteCodes = useDeleteCommonCodes()
  const queryClient = useQueryClient()

  // 편집 상태 — 조회 결과에 편집 셀 변경을 얹는다. 선택 비우기는 뷰 몫([조회]·[초기화]·확인 성공 때 비운다) — 배선에 그 슬롯은 없다.
  const [rows, setRows] = useState<EditableRow[]>([])
  const newSeqRef = useRef(0)
  const prevFetchedRef = useRef<CommonCodeRow[]>([])
  useEffect(() => {
    // 남은 구간이 붙은 것이면 붙은 행만 덧붙인다 — 받는 동안 고친 셀을 지우지 않는다.
    const tail = appendedRows(prevFetchedRef.current, fetchedRows)
    prevFetchedRef.current = fetchedRows
    if (tail) {
      setRows((prev) => [...prev, ...tail.map(toEditable)])
      return
    }
    // 저장 뒤 재조회(수정자·수정일시 반영)가 [추가]한 미저장 행을 지우지 않게 앞에 남긴다(리뷰 sookeunwoo). [조회]·[초기화]는 그 전에 걷는다.
    setRows((prev) => [...prev.filter((r) => r._id == null), ...fetchedRows.map(toEditable)])
  }, [fetchedRows])

  /** 저장 대상 — 선택 행을 편집 반영본에서 되찾는다(뷰가 넘긴 행은 표시용 사본이다). */
  const selectedRows = useCallback(
    (ctx: ActionCtx): EditableRow[] => {
      const picked = new Set(ctx.selected.map(rowKeyOf))
      return rows.filter((r) => picked.has(r.key))
    },
    [rows],
  )

  // 화면관리 그리드 설정(LOGISPRD-17254) — 표 별표(생성본 permCols)와 [저장] 필수 검사가 같은 forTable(TABLE_ID).columns 를 지난다.
  //  BE 필수 칸(BE_REQUIRED_KEYS)은 설정이 필수를 꺼도 별표·검사에 남긴다.
  const permissionSlot = usePermissionSlot('sy10', GRID_CDS)
  const permission = useMemo(() => withRequired(permissionSlot, { [TABLE_ID]: BE_REQUIRED_KEYS }), [permissionSlot])

  // ── 뷰 배선(Wiring) — 배선 객체는 매 렌더 새로 만든다(문서 51). ──
  const wiring: Wiring = {
    permission,
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { [TABLE_ID]: coreTableLayoutSlot(GRID_CDS[TABLE_ID]) },
    rows: {
      // 전량을 구간으로 받아 뷰가 1만 건씩 클라이언트 페이징한다(LOGISPRD-15271). total 없이 주면 뷰가 페이지를 자르고 목록 검색을 전량에 건다
      //  (total 을 주면 서버 페이징 — 생성본 `ROWS_SERVERPAGED`) — 첫 구간·나머지 구간·재조회 중에는 받은 행 위에 로더를 띄워 표를 막는다(LOGISPRD-15390). 받는 만큼 '전체 N' 이 늘어나는 게 진행 표시다.
      ROWS: () => ({ rows, loading: isFirstLoading || isFetchingRest }),
    },
    actions: {
      /** [조회] — 화주는 필수다(옛 FilterArea required). 조건이 바뀌면 새 키 쿼리가 받고, 같은 조건이면 캐시라 그때만 무효화한다(둘 다 하면 옛 조건으로 한 번 더 나간다).
       *  [추가]한 미저장 행은 걷는다 — 응답이 같으면 data 참조가 그대로라(structuralSharing) effect 가 안 돌아 서버에 없는 행이 남는다(리뷰 gyoungjun). */
      search: async (filters) => {
        const next = lockShipper(toParams(filters))
        if (!next.shipperCode) {
          notice.invalid(MESSAGES.noShipper)
          return false // 조회를 안 했으니 뷰가 페이저·선택·검색칸을 리셋하지 않게(생성본은 false 만 건너뛴다)
        }
        bootedRef.current = true // 사용자가 먼저 조회했으면 진입 기본값이 뒤늦게 덮지 않게 한다.
        persist(next)
        const same = sameParams(next, params)
        if (same) {
          // 같은 조건 [조회]는 편집을 버린다(1.0) — 응답이 같으면 받은 행 참조가 그대로라 effect 가 안 돌아, 누르는 자리에서 받은 행으로 되돌린다(#1017 리뷰).
          setRows(fetchedRows.map(toEditable))
          prevFetchedRef.current = fetchedRows
        } else {
          setRows((prev) => prev.filter((r) => r._id != null))
          prevFetchedRef.current = [] // 새 조건은 통째로 바꾼다
        }
        // 조건이 바뀌면 그 키의 지난 캐시(1분)를 지우고 옮긴다 — 안 지우면 이전 조건으로 돌아왔을 때 다시 읽지 않고 캐시를 그린다(ST09 와 같다).
        if (!same) queryClient.removeQueries({ queryKey: commonCodeQueryKeys.list(next), exact: true })
        setParams(next)
        if (same) await queryClient.invalidateQueries({ queryKey: commonCodeQueryKeys.list(next) })
      },
      /** 편집 셀 — 표는 보이는 행만 돌려준다. 행 키로 찾아 바뀐 것만 갈아 끼운다. */
      ROWS_rowsChange: (changed) => {
        const byKey = new Map(changed.map((r) => [rowKeyOf(r), r]))
        setRows((prev) => prev.map((r) => ({ ...r, ...(byKey.get(r.key) ?? {}) }) as EditableRow))
      },
      /** [추가](sy07AddButton 슬롯, 컨덕터 #592) — 빈 행을 맨 위에. 화주 = 조회 화주(고정 계정은 현재 화주). */
      sy07AddButton: ({ defaults }) => {
        newSeqRef.current += 1
        const shipper = isFreeShipper ? String(params.shipperCode ?? '') : currentShipper
        setRows((prev) => [
          { SPR_NM: shipper, CODE_GRP: '', CODE: '', CODE_DESC: '', INS_DATE: '', INS_BY: '', UPD_DATE: '', UPD_BY: '', ...defaults, ...permission.forTable(TABLE_ID).presets, key: `new-${newSeqRef.current}`, __new: true },
          ...prev,
        ])
      },
      /** [저장] 확인 — 선택 행을 행별로 반영한다(새 행 POST · 기존 행 PUT, 옛 onSave). 필수 칸(화면관리 설정 + BE 필수 3칸)·화주 잠금은 옛 화면도 뷰도 안 막아 여기서 지킨다.
       *  검증 실패도 안내 뒤 throw(계약: 확인창 유지 + 선택 유지 — 생성본은 false 를 "성공인데 토스트 생략"으로 읽어 선택을 비운다,
       *  가이드 52 규칙 10 · 2026-10-02 QA: 필수 빈칸 경고 뒤 체크가 전부 풀리던 것). API 실패도 throw. 실패 지점에서 멈추므로 앞서 등록된 새 행은 미저장 목록에서 뺀다 —
       *  안 빼면 다시 저장할 때 중복(400)이 난다. 실패 때는 재조회하지 않는다 — 재조회(useEffect([data]))가 실패·미전송 행의 편집을 서버 값으로 덮어
       *  다시 [확인]하면 옛 값이 "저장 완료"로 나간다(리뷰). 뺀 새 행은 다음 [조회] 때 서버 행으로 보인다. */
      sy07SaveConfirm_ok: async (ctx) => {
        // 받는 중·일부 실패면 안내하고 확인창을 유지한다(throw — false 는 생성본이 성공으로 읽는다)
        if (!guardAction()) throw new Error(FETCH_ALL_MESSAGES.fetching)
        const targets = selectedRows(ctx)
        if (targets.length === 0) {
          throw invalidError(MESSAGES.noSelection)
        }
        const required = permission.forTable(TABLE_ID).columns(SAVE_REQUIRED_CANDIDATES)
        if (findMissingRequired(targets, required)) {
          throw invalidError(MESSAGES.requiredCells(requiredTitles(required)))
        }
        // 화주코드 셀이 편집 가능하다 — 화주 고정 계정이 다른 화주로 고쳐 저장하면 권한 밖 쓰기가 된다(저장 API 는 body 의 shipperCode 를 그대로 받는다).
        if (!isFreeShipper && targets.some((r) => r.SPR_NM !== currentShipper)) {
          throw invalidError(MESSAGES.shipperLocked)
        }
        const dropPosted = (done: number) => {
          const posted = new Set(targets.slice(0, done).filter((r) => r._id == null).map((r) => r.key))
          setRows((prev) => prev.filter((r) => !posted.has(r.key)))
        }
        try {
          await saveCodes.mutateAsync(targets)
          dropPosted(targets.length)
          // [MOMS 공통] 저장 후 조회조건 리셋 금지 — 서버 반영값(새 행 id·수정자·수정일시)만 다시 읽는다.
          prevFetchedRef.current = [] // 재조회는 통째로 바꾼다
          await queryClient.invalidateQueries({ queryKey: commonCodeQueryKeys.all })
        } catch (e) {
          const done = doneOf(e)
          dropPosted(done)
          const msg = done > 0 ? MESSAGES.partialFailed(targets.length, done, apiErrMessage(e)) : apiErrMessage(e)
          throw failureError(msg)
        }
      },
      /** [삭제] 확인 — 미저장 새 행은 화면에서만 지우고, 저장된 행은 행별 DELETE 한다. 실패·안내 규약은 [저장]과 같다(실패 때 재조회 대신 지운 행만 뺀다).
       *  'ALL'(전체공통) 행 권한(HO MASTER 만)은 BE 가 판정한다 — 거절 문구는 BE meta.message 그대로 띄운다. */
      sy07DeleteConfirm_ok: async (ctx) => {
        if (!guardAction()) throw new Error(FETCH_ALL_MESSAGES.fetching)
        const targets = selectedRows(ctx)
        // 잠금은 저장된 행만 — 미저장 새 행은 API 없이 화면에서만 지운다.
        if (!isFreeShipper && targets.some((r) => r._id != null && r.SPR_NM !== currentShipper)) {
          throw invalidError(MESSAGES.deleteShipperLocked)
        }
        const local = new Set(targets.filter((r) => r._id == null).map((r) => r.key))
        setRows((prev) => prev.filter((r) => !local.has(r.key)))
        const ids = targets.filter((r): r is SavedRow => r._id != null).map((r) => r._id)
        if (ids.length === 0) return
        try {
          await deleteCodes.mutateAsync(ids)
          prevFetchedRef.current = [] // 재조회는 통째로 바꾼다
          await queryClient.invalidateQueries({ queryKey: commonCodeQueryKeys.all })
        } catch (e) {
          const done = doneOf(e)
          const deleted = new Set(ids.slice(0, done))
          setRows((prev) => prev.filter((r) => r._id == null || !deleted.has(r._id)))
          const msg = done > 0 ? MESSAGES.partialFailed(ids.length, done, apiErrMessage(e)) : apiErrMessage(e)
          throw failureError(msg)
        }
      },
    },
    options: { SY07SHIPPER_OPTIONS: () => shipperOptions as Option[] },
    /** 화주 고정 계정은 화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 보이는 것만 잠그므로 값은 기본값·옵션 축소가 따로 고정한다 */
    filterDisabled: isFreeShipper ? undefined : { SPR_NM: true },
    filterDefaults: { sy07FilterCard: (demo) => ({ ...demo, SPR_NM: defaultShipper || undefined }) },
    filterInitial: { sy07FilterCard: (defaults) => ({ ...defaults, ...toFilterValues(restoredRef.current) }) },
    useBusy: () => saveCodes.isPending || deleteCodes.isPending,
    /** [초기화] — 저장된 조회 조건과 표까지 비운다(옛 onReset). 다음 진입이 기본값으로 열리게 한다. */
    onReset: () => {
      MenuStorage.menuOnSession.setSearch({})
      menuSession.reload()
      replaceQuery({})
      setRows([]) // 미저장 신규 행까지 비운다(빈 조건은 조회하지 않아 effect 가 안 돈다)
      setParams({})
      bootedRef.current = true // 초기화 뒤에는 진입 기본값을 다시 심지 않는다(옛 화면과 같다).
    },
  }

  // 뷰는 조회영역 기본값(filterDefaults)을 마운트 때 한 번만 읽는다 — 화주 옵션이 오기 전에 그리면 화주 칸이 비고,
  //  key 로 다시 세우면 그 사이 고른 선택이 사라진다(#777 리뷰). 옵션이 정해진 뒤에 한 번만 그린다.
  if (shipperQuery.isPending) return null
  return <CommonCodePageView wiring={wiring} />
}

export default CommonCodeManagement
